import { apiFetch, buildQueryString } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';
import type { User } from '@lognai/core-platform';
import { UserStatus } from '../types/domain-enums';

export interface UserFilter {
  companyId?: string;
  status?: UserStatus;
  department?: string;
  search?: string;
}

export type UserFilters = ListQueryParams<UserFilter>;

export interface PaginatedUsersResult {
  items: User[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface UserCreateInput {
  email: string;
  fullName: string;
  companyId: string;
  username?: string;
  password?: string;
  department?: string;
  jobTitle?: string;
  employeeId?: string;
  phoneNumber?: string;
  status?: UserStatus;
  timezone?: string;
  language?: string;
}

export interface UserUpdateInput {
  fullName?: string;
  department?: string;
  jobTitle?: string;
  employeeId?: string;
  phoneNumber?: string;
  timezone?: string;
  language?: string;
  status?: UserStatus;
}

export interface UserRoleAssignment {
  roleId: string;
  companyId?: string;
  expiresAt?: Date;
}

export interface UserAccessAssignment {
  accessId: string;
  companyId?: string;
  expiresAt?: Date;
}

const BASE_URL = '/api/admin/users';

export const usersService = {
  /**
   * List users with optional filters and server-side pagination.
   *
   * Returns the paginated envelope so callers can render `total`/`totalPages`
   * without a second round trip. The server's listUsers operation reads
   * `page` + `pageSize` from the query string; we map `filters.limit` to
   * `pageSize` so this service composes with the shared `usePagination` hook.
   */
  list: async (filters?: UserFilters): Promise<PaginatedUsersResult> => {
    const queryParams: Record<string, unknown> = { ...(filters as Record<string, unknown>) };
    if (queryParams.limit !== undefined && queryParams.pageSize === undefined) {
      queryParams.pageSize = queryParams.limit;
      delete queryParams.limit;
    }
    const query = buildQueryString(queryParams);
    const res = await apiFetch<PaginatedUsersResult | User[]>(`${BASE_URL}${query}`);
    // Tolerate legacy array shape if any server route ever falls back.
    if (Array.isArray(res)) {
      return { items: res, total: res.length, page: 1, pageSize: res.length || 1, totalPages: 1 };
    }
    return res;
  },

  /**
   * Get a specific user by ID
   */
  getById: async (userId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}`);
  },

  /**
   * Create a new user
   */
  create: async (data: UserCreateInput): Promise<User> => {
    return apiFetch<User>(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a user
   */
  update: async (userId: string, data: UserUpdateInput): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a user
   */
  delete: async (userId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}`, { method: 'DELETE' });
  },

  /**
   * Update user status
   */
  updateStatus: async (userId: string, status: UserStatus): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Get user's roles
   */
  getRoles: async (userId: string): Promise<unknown[]> => {
    return apiFetch<unknown[]>(`${BASE_URL}/${userId}/roles`);
  },

  /**
   * Assign a role to user
   */
  assignRole: async (userId: string, assignment: UserRoleAssignment): Promise<unknown> => {
    return apiFetch<unknown>(`${BASE_URL}/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  },

  /**
   * Bulk assign role to multiple users
   */
  bulkAssignRole: async (
    userIds: string[],
    roleId: string,
    assignment?: Partial<UserRoleAssignment>
  ): Promise<{ assigned: number; failed: number }> => {
    return apiFetch<{ assigned: number; failed: number }>(`${BASE_URL}/bulk/assign-role`, {
      method: 'POST',
      body: JSON.stringify({ userIds, roleId, ...assignment }),
    });
  },

  /**
   * Remove a role from user
   */
  removeRole: async (userId: string, roleId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/roles/${roleId}`, { method: 'DELETE' });
  },

  /**
   * Get user's direct access assignments
   */
  getAccessAssignments: async (userId: string): Promise<unknown[]> => {
    return apiFetch<unknown[]>(`${BASE_URL}/${userId}/access`);
  },

  /**
   * Assign direct access to user
   */
  assignAccess: async (userId: string, assignment: UserAccessAssignment): Promise<unknown> => {
    return apiFetch<unknown>(`${BASE_URL}/${userId}/access`, {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  },

  /**
   * Remove direct access from user
   */
  removeAccess: async (userId: string, accessId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/access/${accessId}`, { method: 'DELETE' });
  },

  /**
   * Get user's effective permissions
   */
  getEffectivePermissions: async (userId: string): Promise<string[]> => {
    return apiFetch<string[]>(`${BASE_URL}/${userId}/permissions`);
  },

  /**
   * Add secondary company to user
   */
  addSecondaryCompany: async (userId: string, companyId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/secondary-companies`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },

  /**
   * Remove secondary company from user
   */
  removeSecondaryCompany: async (userId: string, companyId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/secondary-companies/${companyId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Reset user password (admin)
   */
  resetPassword: async (userId: string, newPassword: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  },

  /**
   * Enable MFA for user
   */
  enableMfa: async (userId: string): Promise<{ secret: string; qrCode: string }> => {
    return apiFetch<{ secret: string; qrCode: string }>(`${BASE_URL}/${userId}/mfa/enable`, {
      method: 'POST',
    });
  },

  /**
   * Disable MFA for user
   */
  disableMfa: async (userId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/mfa/disable`, { method: 'POST' });
  },
};
